<template>
    <div class="grid sm:grid-cols-2 gap-x-4">
        <div class="mx-auto text-center">
            <label class="block text-sm font-bold text-gray-700">
                Quantity
            </label>

            <div class="mt-2 relative inline-flex shadow-sm rounded-md">
                <button
                    data-test="decrease-quantity"
                    @click="decreaseQuantity"
                    type="button"
                    :class="[
                        quantity > 1
                            ? 'bg-white hover:bg-gray-50 text-gray-500'
                            : 'bg-gray-100 text-gray-300 cursor-not-allowed',
                        'relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium focus:z-10 focus:outline-none',
                    ]"
                >
                    <span class="sr-only">-1</span>
                    <MinusIcon class="h-5 w-5" aria-hidden="true" />
                </button>

                <Listbox class="-ml-px" as="div" v-model="selectedQuantity">
                    <ListboxLabel />
                    <div class="relative">
                        <ListboxButton
                            class="bg-white relative w-full border border-gray-300 px-6 py-2 text-left cursor-default focus:outline-none font-semibold text-sm text-gray-700"
                        >
                            <span class="block">{{ selectedQuantity }}</span>
                        </ListboxButton>

                        <transition
                            leave-active-class="transition ease-in duration-100"
                            leave-from-class="opacity-100"
                            leave-to-class="opacity-0"
                        >
                            <ListboxOptions
                                class="absolute z-20 mt-1 w-full bg-white border border-gray-300 shadow-lg max-h-36 rounded-md py-1 overflow-y-scroll overflow-x-hidden focus:outline-none text-sm"
                            >
                                <ListboxOption
                                    as="template"
                                    v-for="quantityOption in product
                                        .default_product_option
                                        .maximum_quantity"
                                    :key="quantityOption"
                                    :value="quantityOption.toString()"
                                    v-slot="{ active, selected }"
                                >
                                    <li
                                        :class="[
                                            active
                                                ? 'text-white bg-nutreance-green'
                                                : 'text-gray-700',
                                            'cursor-default select-none py-2 px-2 flex justify-between items-center space-x-1',
                                        ]"
                                    >
                                        <span
                                            :class="[
                                                selected
                                                    ? 'font-semibold'
                                                    : 'font-normal',
                                            ]"
                                        >
                                            {{ quantityOption }}
                                        </span>
                                        <span
                                            v-if="selected"
                                            :class="[
                                                active
                                                    ? 'text-white'
                                                    : 'text-nutreance-green',
                                            ]"
                                        >
                                            <CheckIcon
                                                class="h-5 w-5"
                                                aria-hidden="true"
                                            />
                                        </span>
                                    </li>
                                </ListboxOption>
                            </ListboxOptions>
                        </transition>
                    </div>
                </Listbox>

                <button
                    data-test="increase-quantity"
                    @click="increaseQuantity"
                    type="button"
                    class="-ml-px relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10 focus:outline-none"
                >
                    <span class="sr-only">+1</span>
                    <PlusIcon class="h-5 w-5" aria-hidden="true" />
                </button>
            </div>

            <p class="px-3 py-1 text-sm text-red-600" v-if="errors.quantity">
                {{ errors.quantity }}
            </p>
        </div>

        <div class="mt-6 sm:mt-0 mb-4 sm:mb-0 mx-auto text-center">
            <label class="block text-sm font-bold text-gray-700">
                {{ subscribe ? "Delivery Every" : "Auto-Delivery" }}
            </label>

            <div class="mt-2 relative z-0 inline-flex shadow-sm rounded-md">
                <button
                    data-test="previous-subscription-option"
                    @click="previousSubscriptionOption"
                    type="button"
                    :class="[
                        previousSubscriptionOptionExists
                            ? 'bg-white hover:bg-gray-50 text-gray-500'
                            : 'bg-gray-100 text-gray-300 cursor-not-allowed',
                        '-ml-px relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium focus:z-10 focus:outline-none',
                    ]"
                >
                    <span class="sr-only">Previous Subscription Option</span>
                    <MinusIcon class="h-5 w-5" aria-hidden="true" />
                </button>

                <Listbox
                    class="-ml-px w-full min-w-20"
                    as="div"
                    v-model="selectedSubscriptionOptionId"
                >
                    <ListboxLabel />
                    <div class="relative">
                        <ListboxButton
                            :class="[
                                subscriptionOption(subscriptionOptionId)
                                    .how_often
                                    ? 'px-6'
                                    : 'px-8',
                                'bg-white relative w-full border border-gray-300 py-2 text-center cursor-default focus:outline-none font-semibold text-sm text-gray-700',
                            ]"
                        >
                            <span class="block">
                                {{
                                    subscriptionOption(subscriptionOptionId)
                                        .how_often
                                        ? `${subscriptionOption(subscriptionOptionId).how_often} Days`
                                        : "Off"
                                }}
                            </span>
                        </ListboxButton>

                        <transition
                            leave-active-class="transition ease-in duration-100"
                            leave-from-class="opacity-100"
                            leave-to-class="opacity-0"
                        >
                            <ListboxOptions
                                class="absolute z-20 mt-1 w-full bg-white border border-gray-300 shadow-lg max-h-36 rounded-md py-1 overflow-y-scroll overflow-x-hidden focus:outline-none text-sm"
                            >
                                <ListboxOption
                                    as="template"
                                    v-for="subscriptionOptionSelection in product
                                        .default_product_option
                                        .subscription_options"
                                    :key="subscriptionOptionSelection.id"
                                    :value="subscriptionOptionSelection.id"
                                    v-slot="{ active, selected }"
                                >
                                    <li
                                        :class="[
                                            active
                                                ? 'text-white bg-nutreance-green'
                                                : 'text-gray-700',
                                            'cursor-default select-none py-2 px-2 flex justify-between items-center space-x-1',
                                        ]"
                                    >
                                        <span
                                            :class="[
                                                selected
                                                    ? 'font-semibold'
                                                    : 'font-normal',
                                            ]"
                                        >
                                            {{
                                                subscriptionOptionSelection.how_often
                                                    ? `${subscriptionOptionSelection.how_often} Days`
                                                    : "Off"
                                            }}
                                        </span>
                                        <span
                                            v-if="selected"
                                            :class="[
                                                active
                                                    ? 'text-white'
                                                    : 'text-nutreance-green',
                                            ]"
                                        >
                                            <CheckIcon
                                                class="h-5 w-5"
                                                aria-hidden="true"
                                            />
                                        </span>
                                    </li>
                                </ListboxOption>
                            </ListboxOptions>
                        </transition>
                    </div>
                </Listbox>

                <button
                    data-test="next-subscription-option"
                    @click="nextSubscriptionOption"
                    type="button"
                    :class="[
                        nextSubscriptionOptionExists
                            ? 'bg-white hover:bg-gray-50 text-gray-500'
                            : 'bg-gray-100 text-gray-300 cursor-not-allowed',
                        '-ml-px relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium focus:z-10 focus:outline-none',
                    ]"
                >
                    <span class="sr-only">Next Subscription Option</span>
                    <PlusIcon class="h-5 w-5" aria-hidden="true" />
                </button>
            </div>

            <p
                class="px-3 py-1 text-sm text-red-600"
                v-if="errors.subscription_option_id"
            >
                {{ errors.subscription_option_id }}
            </p>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import {
    Listbox,
    ListboxButton,
    ListboxLabel,
    ListboxOption,
    ListboxOptions,
} from "@headlessui/vue";
import { CheckIcon, MinusIcon, PlusIcon } from "@heroicons/vue/solid";

const props = defineProps<{
    errors?: any;
    product?: any;
    subscribe?: boolean;
    quantity: number;
    subscriptionOptionId: number;
}>();

const emit = defineEmits<{
    (e: "errorMaxQuantity", payload: { quantity: string }): void;
    (e: "quantityUpdated", quantity: number): void;
    (e: "subscriptionOptionUpdated", subscriptionOptionId: number): void;
    (e: "unsubscribe"): void;
}>();

const selectedQuantity = ref(props.quantity.toString());
const selectedSubscriptionOptionId = ref(props.subscriptionOptionId);

watch(
    () => selectedQuantity.value,
    (quantity) => {
        updateQuantity(parseInt(quantity));
    },
);

watch(
    () => selectedSubscriptionOptionId.value,
    (subscriptionOptionId) => {
        updateSubscriptionOption(subscriptionOptionId);
    },
);

watch(
    () => props.quantity,
    (quantity) => {
        selectedQuantity.value = quantity.toString();
    },
);

watch(
    () => props.subscriptionOptionId,
    (subscriptionOptionId) => {
        selectedSubscriptionOptionId.value = subscriptionOptionId;
    },
);

watch(
    () => props.subscribe,
    () => (selectedSubscriptionOptionId.value = props.subscriptionOptionId),
);

function decreaseQuantity() {
    if (props.quantity === 1) {
        return;
    }

    updateQuantity(props.quantity - 1);
}

function increaseQuantity() {
    if (
        props.quantity + 1 >
        props.product.default_product_option.maximum_quantity
    ) {
        return emit("errorMaxQuantity", {
            quantity: `The maximum quantity allowed is ${props.product.default_product_option.maximum_quantity}`,
        });
    }

    updateQuantity(props.quantity + 1);
}

function updateQuantity(quantity: number) {
    emit("quantityUpdated", quantity);
}

function previousSubscriptionOption() {
    if (!previousSubscriptionOptionExists.value) {
        return emit("unsubscribe");
    }

    updateSubscriptionOption(props.subscriptionOptionId - 1);
}

function nextSubscriptionOption() {
    if (nextSubscriptionOptionExists.value) {
        updateSubscriptionOption(props.subscriptionOptionId + 1);
    }
}

function updateSubscriptionOption(subscriptionOptionId: number) {
    emit("subscriptionOptionUpdated", subscriptionOptionId);
}

function subscriptionOption(id: number) {
    for (const key in props.product.default_product_option
        .subscription_options) {
        if (
            props.product.default_product_option.subscription_options[key]
                .id === id
        ) {
            return props.product.default_product_option.subscription_options[
                key
            ];
        }
    }

    return null;
}

const previousSubscriptionOptionExists = computed(
    () => !!subscriptionOption(props.subscriptionOptionId - 1),
);

const nextSubscriptionOptionExists = computed(
    () => !!subscriptionOption(props.subscriptionOptionId + 1),
);
</script>
